import { useState, useRef, useCallback } from "react";

// ─── helpers ────────────────────────────────────────────────────────────────

/** Convert zero-based column index to spreadsheet letter(s): 0→A, 25→Z, 26→AA */
function toAlpha(i) {
  let s = "";
  let n = i;
  while (n >= 0) {
    s = String.fromCharCode(65 + (n % 26)) + s;
    n = Math.floor(n / 26) - 1;
  }
  return s;
}

/** Format any cell value for display */
function formatCell(val) {
  if (val === true) return "Yes";
  if (val === false) return "No";
  if (val === null || val === undefined) return "";
  return String(val);
}

// ─── constants ───────────────────────────────────────────────────────────────

const ROW_H      = 36;   // px — default row height
const COL_W      = 120;  // px — default column width
const HEADER_W   = 48;   // px — row-number column width
const HEADER_H   = 28;   // px — column-header row height

// ─── styles (plain objects, no CSS-in-JS lib needed) ────────────────────────

const S = {
  root: {
    fontFamily: "'JetBrains Mono', 'Fira Mono', 'Cascadia Code', monospace",
    fontSize: 13,
    userSelect: "none",
    border: "1px solid #d0d0d0",
    borderRadius: 6,
    overflow: "hidden",
    display: "inline-flex",
    flexDirection: "column",
    width: "100%",
  },
  formulaBar: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "0 10px",
    height: 30,
    background: "#f8f8f8",
    borderBottom: "1px solid #d0d0d0",
    flexShrink: 0,
  },
  cellRef: {
    fontSize: 12,
    fontWeight: 600,
    color: "#1a73e8",
    minWidth: 52,
    letterSpacing: "0.02em",
  },
  divider: {
    width: 1,
    height: 16,
    background: "#d0d0d0",
    flexShrink: 0,
  },
  cellVal: {
    fontSize: 12,
    color: "#333",
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  scrollWrap: {
    overflow: "auto",
    flex: 1,
    maxHeight: 400,   // adjust to taste
    position: "relative",
  },
  table: {
    borderCollapse: "collapse",
    tableLayout: "fixed",
  },
  thCorner: {
    position: "sticky",
    top: 0,
    left: 0,
    zIndex: 4,
    width: HEADER_W,
    minWidth: HEADER_W,
    height: HEADER_H,
    background: "#f0f0f0",
    borderRight: "1px solid #d0d0d0",
    borderBottom: "1px solid #bbb",
    boxSizing: "border-box",
  },
  thCol: (isActive) => ({
    position: "sticky",
    top: 0,
    zIndex: 3,
    width: COL_W,
    minWidth: COL_W,
    height: HEADER_H,
    background: isActive ? "#d3e4fd" : "#f0f0f0",
    color: isActive ? "#1a73e8" : "#555",
    textAlign: "center",
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: "0.04em",
    borderRight: "1px solid #d0d0d0",
    borderBottom: "1px solid #bbb",
    cursor: "default",
    transition: "background 0.1s",
    boxSizing: "border-box",
    padding: "0 4px",
  }),
  thRow: (isActive) => ({
    position: "sticky",
    left: 0,
    zIndex: 2,
    width: HEADER_W,
    minWidth: HEADER_W,
    height: ROW_H,
    background: isActive ? "#d3e4fd" : "#f0f0f0",
    color: isActive ? "#1a73e8" : "#777",
    textAlign: "center",
    fontSize: 12,
    fontWeight: 500,
    borderRight: "1px solid #bbb",
    borderBottom: "1px solid #d0d0d0",
    cursor: "default",
    transition: "background 0.1s",
    boxSizing: "border-box",
  }),
  tdCell: (isSelected, customBg, wrap) => ({
    width: COL_W,
    minWidth: COL_W,
    height: wrap ? undefined : ROW_H,
    minHeight: ROW_H,
    padding: "0 7px",
    fontSize: 13,
    color: "#222",
    verticalAlign: "middle",
    borderRight: "1px solid #e8e8e8",
    borderBottom: "1px solid #e8e8e8",
    cursor: "default",
    background: isSelected
      ? "rgba(26,115,232,0.10)"
      : customBg || "#ffffff",
    outline: isSelected ? "2px solid #1a73e8" : "none",
    outlineOffset: -2,
    transition: "background 0.08s",
    boxSizing: "border-box",
    // wrap control
    whiteSpace: wrap ? "pre-wrap" : "nowrap",
    overflow: wrap ? "visible" : "hidden",
    textOverflow: wrap ? "clip" : "ellipsis",
    wordBreak: wrap ? "break-word" : "normal",
  }),
};

// ─── component ───────────────────────────────────────────────────────────────

/**
 * SpreadsheetGrid
 *
 * Props:
 *   rows        — array of arrays  (required)
 *   columns     — string[]         (optional; falls back to A, B, C …)
 *   cellColors  — object           (optional; keys like "rowIdx-colIdx" → CSS color string)
 *   wrap        — boolean          (optional; default false)
 */
export default function SpreadsheetGrid({
  rows = [],
  columns,
  cellColors = {},
  wrap = false,
}) {
  const [selRow, setSelRow] = useState(-1);
  const [selCol, setSelCol] = useState(-1);
  const scrollRef = useRef(null);

  // derive column names
  const numCols = rows[0]?.length ?? (columns?.length ?? 0);
  const colNames = columns?.length
    ? columns
    : Array.from({ length: numCols }, (_, i) => toAlpha(i));

  const handleCellClick = useCallback((ri, ci, val) => {
    setSelRow(ri);
    setSelCol(ci);
  }, []);

  const selectedVal =
    selRow >= 0 && selCol >= 0
      ? formatCell(rows[selRow]?.[selCol])
      : "";

  const cellRefLabel =
    selRow >= 0 && selCol >= 0
      ? `${colNames[selCol] ?? toAlpha(selCol)}${selRow + 1}`
      : "—";

  return (
    <div style={S.root}>
      {/* Formula bar */}
      <div style={S.formulaBar}>
        <span style={S.cellRef}>{cellRefLabel}</span>
        <div style={S.divider} />
        <span style={S.cellVal}>
          {selRow >= 0 ? selectedVal || "\u00a0" : "Click a cell"}
        </span>
      </div>

      {/* Scrollable grid */}
      <div style={S.scrollWrap} ref={scrollRef}>
        <table style={S.table}>
          {/* Column headers */}
          <thead>
            <tr>
              <th style={S.thCorner} />
              {colNames.map((name, ci) => (
                <th key={ci} style={S.thCol(ci === selCol)}>
                  {name}
                </th>
              ))}
            </tr>
          </thead>

          {/* Data rows */}
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri}>
                {/* Row number */}
                <td style={S.thRow(ri === selRow)}>{ri + 1}</td>

                {/* Cells */}
                {colNames.map((_, ci) => {
                  const val = formatCell(row[ci]);
                  const isSelected = ri === selRow && ci === selCol;
                  const customBg = cellColors[`${ri}-${ci}`] ?? null;
                  return (
                    <td
                      key={ci}
                      style={S.tdCell(isSelected, customBg, wrap)}
                      title={val}
                      onClick={() => handleCellClick(ri, ci, val)}
                    >
                      {val}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}